import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Users, Activity, IndianRupee, Pill, Package, AlertTriangle, CalendarDays,
  ArrowRight, Plus, ClipboardList, TrendingUp, Loader2, RefreshCw, Clock,
  Sparkles, MoreHorizontal, Inbox,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ReceptionLayout from '../components/ReceptionLayout';
import { Button } from '../components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { useTenant } from '../contexts/TenantContext';
import GlobalPatientSearch from '../components/GlobalPatientSearch';
import BookingRequestsDrawer from '../components/BookingRequestsDrawer';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function ReceptionHomePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingBookings, setPendingBookings] = useState(0);
  const [bookingDrawerOpen, setBookingDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const brand = tenant?.branding?.primary_color || '#0f766e';

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await ax.get('/api/clinic-ops/reception-home');
      setData(data);
    } catch (_) {
      setData(null);
    }
    setLoading(false);
  };
  const refreshBookings = async () => {
    try {
      const { data } = await ax.get('/api/clinic-ops/booking-requests', { params: { status: 'tentative' } });
      setPendingBookings((data?.requests || []).length);
    } catch (_) { /* silent */ }
  };
  useEffect(() => {
    refresh();
    refreshBookings();
    const t = setInterval(() => { refresh(); refreshBookings(); }, 30000);
    return () => clearInterval(t);
  }, []);

  const today = new Date().toLocaleDateString('en-IN', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Kolkata',
  });

  return (
    <ReceptionLayout
      pageTitle="Reception Dashboard"
      pageSubtitle={today}
      rightActions={
        <Button variant="outline" size="sm" onClick={refresh} disabled={loading} data-testid="rh-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Refresh
        </Button>
      }
    >
      <div className="p-7 max-w-[1400px] mx-auto space-y-6" data-testid="reception-home">
        {/* Global Patient Search */}
        <div className="bg-white border rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[260px] max-w-2xl">
              <GlobalPatientSearch />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBookingDrawerOpen(true)}
              className="gap-1.5 relative"
              data-testid="reception-bookings-btn"
            >
              <Inbox className="h-3.5 w-3.5" /> Bookings
              {pendingBookings > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-white" data-testid="bookings-badge">
                  {pendingBookings}
                </span>
              )}
            </Button>

            {/* Consolidated menu — Reports, Roster, Inventory, etc. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5" data-testid="reception-more-menu">
                  <MoreHorizontal className="h-3.5 w-3.5" /> More
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Operations
                </DropdownMenuLabel>
                <DropdownMenuItem onSelect={() => navigate('/reception/reports')} data-testid="reception-menu-reports">
                  <ClipboardList className="h-4 w-4 mr-2 text-blue-600" />
                  <div className="flex-1">
                    <div className="text-sm font-medium">Visit Reports</div>
                    <div className="text-[10px] text-zinc-500">Date-wise revenue + check-ins</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate('/reception/roster')} data-testid="reception-menu-roster">
                  <CalendarDays className="h-4 w-4 mr-2 text-emerald-600" />
                  <div className="flex-1">
                    <div className="text-sm font-medium">Duty Roster</div>
                    <div className="text-[10px] text-zinc-500">Schedule doctor shifts</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Stock
                </DropdownMenuLabel>
                <DropdownMenuItem onSelect={() => navigate('/inventory')} data-testid="reception-menu-inventory">
                  <Package className="h-4 w-4 mr-2 text-amber-600" />
                  <div className="flex-1">
                    <div className="text-sm font-medium">Inventory</div>
                    <div className="text-[10px] text-zinc-500">Medicines · stock · GST</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate('/pharmacy')} data-testid="reception-menu-pharmacy">
                  <Pill className="h-4 w-4 mr-2 text-rose-600" />
                  <div className="flex-1">
                    <div className="text-sm font-medium">Pharmacy</div>
                    <div className="text-[10px] text-zinc-500">Dispense pending Rx</div>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* KPI ROW */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi
            label="Patients today"
            value={data?.patients_today ?? '—'}
            sub={`${data?.queue?.total ?? 0} total check-ins`}
            icon={<Users className="h-5 w-5" />}
            tone="emerald"
            testid="kpi-patients"
          />
          <Kpi
            label="In queue"
            value={(data?.queue?.waiting ?? 0) + (data?.queue?.in_consult ?? 0)}
            sub={`${data?.queue?.waiting ?? 0} waiting · ${data?.queue?.in_consult ?? 0} in consult`}
            icon={<Activity className="h-5 w-5" />}
            tone="blue"
            testid="kpi-queue"
          />
          <Kpi
            label="Revenue today"
            value={fmt(data?.revenue?.grand_total)}
            sub={`OP ${fmt(data?.revenue?.consultation?.total)} · Rx ${fmt(data?.revenue?.pharmacy?.total)}`}
            icon={<IndianRupee className="h-5 w-5" />}
            tone="purple"
            testid="kpi-revenue"
          />
          <Kpi
            label="Pending Rx dispense"
            value={data?.pending_rx_count ?? 0}
            sub={(data?.low_stock || []).length > 0 ? `${(data.low_stock || []).length} low-stock items` : 'All stock healthy'}
            icon={<Pill className="h-5 w-5" />}
            tone="amber"
            testid="kpi-pending-rx"
          />
        </div>

        {/* HERO + QUICK ACTIONS */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 relative overflow-hidden rounded-2xl text-white p-7"
               style={{ background: `linear-gradient(135deg, ${brand} 0%, ${brand}d8 60%, ${brand}a0 100%)` }}>
            <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full border-[12px] border-white/10" />
            <div className="absolute top-10 right-20 h-20 w-20 rounded-full border-[8px] border-white/10" />
            <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
                 style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '14px 14px' }} />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 bg-white/15 ring-1 ring-white/25 backdrop-blur px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold mb-3">
                <Sparkles className="h-3 w-3" /> Smart Front-Office
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight max-w-xl" style={{ fontFamily: 'Manrope' }}>
                Welcome back. Let's get patients moving.
              </h2>
              <p className="text-white/85 text-sm mt-2 max-w-xl leading-relaxed">
                Check in patients, manage today's queues, dispense pharmacy orders and watch revenue roll up — all from one console.
              </p>
              <div className="flex flex-wrap gap-2 mt-5">
                <QA onClick={() => navigate('/reception')} icon={<ClipboardList className="h-3.5 w-3.5" />} label="New check-in" testid="qa-checkin" />
                <QA onClick={() => navigate('/pharmacy')} icon={<Pill className="h-3.5 w-3.5" />} label="Dispense Rx" testid="qa-rx" />
                <QA onClick={() => navigate('/inventory')} icon={<Package className="h-3.5 w-3.5" />} label="Inventory" testid="qa-inv" />
              </div>
            </div>
          </div>

          {/* Revenue split */}
          <div className="rounded-2xl bg-white border p-5" data-testid="rh-revenue-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-zinc-900 text-sm" style={{ fontFamily: 'Manrope' }}>Revenue split</h3>
              <TrendingUp className="h-4 w-4 text-emerald-600" />
            </div>
            <RevenueBar
              cons={data?.revenue?.consultation?.total || 0}
              pharm={data?.revenue?.pharmacy?.total || 0}
              brand={brand}
            />
            <div className="mt-4 space-y-2">
              <MethodRow label="Consultation" by_method={data?.revenue?.consultation?.by_method} />
              <MethodRow label="Pharmacy" by_method={data?.revenue?.pharmacy?.by_method} />
            </div>
          </div>
        </div>

        {/* SESSIONS + ALERTS */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-2xl bg-white border p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-zinc-900 text-sm flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <CalendarDays className="h-4 w-4 text-zinc-500" /> Today's sessions
              </h3>
              <Button size="sm" variant="ghost" onClick={() => navigate('/reception')}
                className="text-xs h-7" data-testid="rh-open-desk">
                Open desk <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
            <div className="space-y-2">
              {(data?.sessions || []).length === 0 ? (
                <EmptyHint icon={<CalendarDays className="h-7 w-7" />} text="No sessions today — head to the Reception Desk to start one." />
              ) : (
                data.sessions.map(s => (
                  <div key={s.id} className="flex items-center gap-3 p-3 rounded-lg border hover:bg-zinc-50/60 transition"
                       data-testid={`rh-session-${s.id}`}>
                    <div className="h-9 w-9 rounded-lg flex items-center justify-center text-[11px] font-extrabold text-white"
                         style={{ background: brand }}>
                      {s.queue_counter || 0}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm text-zinc-900 truncate">{s.doctor_name}</div>
                      <div className="text-[11px] text-zinc-500 flex items-center gap-2 mt-0.5">
                        <Clock className="h-3 w-3" /> {s.start_time}–{s.end_time}
                        <span className="text-zinc-300">·</span>
                        <span>{s.booked_count} booked</span>
                        <span className="text-zinc-300">·</span>
                        <span>Fee {fmt(s.consultation_fee)}</span>
                      </div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${
                      s.status === 'open' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-zinc-100 text-zinc-500 border border-zinc-200'
                    }`}>{s.status}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="rounded-2xl bg-white border p-5" data-testid="rh-alerts">
            <h3 className="font-bold text-zinc-900 text-sm flex items-center gap-2 mb-3" style={{ fontFamily: 'Manrope' }}>
              <AlertTriangle className="h-4 w-4 text-amber-500" /> Inventory alerts
            </h3>
            <Alerts title="Low stock" items={data?.low_stock} field="quantity" suffix="" emptyText="All stock healthy." testidPrefix="rh-low" />
            <div className="mt-4 border-t pt-3">
              <Alerts title="Expiring soon" items={data?.expiring_soon} field="expiry" suffix="" emptyText="Nothing expiring soon." testidPrefix="rh-exp" />
            </div>
            <Button variant="ghost" size="sm" className="w-full mt-3 text-xs h-7" onClick={() => navigate('/inventory')}
              data-testid="rh-go-inv">
              <Package className="h-3 w-3 mr-1" /> Manage inventory
            </Button>
          </div>
        </div>
      </div>
      <BookingRequestsDrawer
        open={bookingDrawerOpen}
        onClose={() => setBookingDrawerOpen(false)}
        onActioned={() => { refresh(); refreshBookings(); }}
      />
    </ReceptionLayout>
  );
}

function Kpi({ label, value, sub, icon, tone, testid }) {
  const tones = {
    emerald: 'from-emerald-50 to-white text-emerald-700 ring-emerald-100',
    blue:    'from-blue-50 to-white text-blue-700 ring-blue-100',
    purple:  'from-purple-50 to-white text-purple-700 ring-purple-100',
    amber:   'from-amber-50 to-white text-amber-700 ring-amber-100',
  };
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${tones[tone]} border ring-1 p-5`} data-testid={testid}>
      <div className="absolute -top-4 -right-4 h-20 w-20 rounded-full bg-white/40" />
      <div className="relative">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] font-bold opacity-80">
          <span className="h-7 w-7 rounded-lg bg-white/80 flex items-center justify-center shadow-sm">{icon}</span>
          {label}
        </div>
        <div className="text-3xl font-extrabold tracking-tight text-zinc-900 mt-3" style={{ fontFamily: 'Manrope' }}>
          {value}
        </div>
        <div className="text-[11px] text-zinc-600 mt-1 truncate">{sub}</div>
      </div>
    </div>
  );
}

function QA({ icon, label, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/30 ring-1 ring-white/30 backdrop-blur px-3.5 py-2 rounded-lg text-xs font-bold tracking-wide transition"
    >
      <Plus className="h-3 w-3" />{icon}{label}
    </button>
  );
}

function RevenueBar({ cons, pharm, brand }) {
  const total = cons + pharm;
  if (total === 0) {
    return <div className="h-2 bg-zinc-100 rounded-full" />;
  }
  const cPct = (cons / total) * 100;
  return (
    <div className="space-y-2">
      <div className="h-2 rounded-full overflow-hidden bg-zinc-100 flex">
        <div style={{ width: `${cPct}%`, background: brand }} />
        <div style={{ width: `${100 - cPct}%`, background: '#10b981' }} />
      </div>
      <div className="flex justify-between text-[10px] uppercase tracking-wider text-zinc-500">
        <span><span className="inline-block h-2 w-2 rounded-full mr-1" style={{ background: brand }} />Consultation</span>
        <span><span className="inline-block h-2 w-2 rounded-full mr-1 bg-emerald-500" />Pharmacy</span>
      </div>
    </div>
  );
}

function MethodRow({ label, by_method }) {
  const entries = Object.entries(by_method || {});
  if (entries.length === 0) {
    return (
      <div className="flex justify-between items-center text-xs">
        <span className="text-zinc-500">{label}</span>
        <span className="text-zinc-300 font-mono">—</span>
      </div>
    );
  }
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {entries.map(([m, v]) => (
          <span key={m} className="text-[10px] bg-zinc-100 px-2 py-1 rounded font-semibold text-zinc-700">
            {m.toUpperCase()} {fmt(v)}
          </span>
        ))}
      </div>
    </div>
  );
}

function Alerts({ title, items, field, suffix, emptyText, testidPrefix }) {
  const list = items || [];
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-2">{title}</div>
      {list.length === 0 ? (
        <p className="text-xs text-zinc-400 italic">{emptyText}</p>
      ) : list.slice(0, 5).map(m => (
        <div key={m.id} className="flex justify-between items-center py-1 text-xs" data-testid={`${testidPrefix}-${m.id}`}>
          <span className="text-zinc-800 truncate">{m.name}</span>
          <span className="font-mono font-bold text-amber-700">{m[field]}{suffix}</span>
        </div>
      ))}
    </div>
  );
}

function EmptyHint({ icon, text }) {
  return (
    <div className="py-8 text-center text-zinc-400">
      <div className="mx-auto inline-block opacity-50">{icon}</div>
      <p className="text-xs mt-2">{text}</p>
    </div>
  );
}
